import {
	Dialog,
	RegularTableViewColumnDefinition,
	TableView,
	Toolbar,
	renderReactDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useMemo
} from "react";

export type NotAllItemsWereDeletedDialogProps = {
	failedItems: readonly FailedItem[];
	showReasons: boolean;
	onClose(): void;
};

export const NotAllItemsWereDeletedDialog = (props: NotAllItemsWereDeletedDialogProps): ReactElement => {
	const {
		failedItems,
		showReasons,
		onClose
	} = props;

	const columnDefinitions = useMemo(
		(): readonly RegularTableViewColumnDefinition[] => {
			const columns: RegularTableViewColumnDefinition[] = [
				{
					title: localize("_ItemName"),
					trackingId: "name"
				}
			];

			if (showReasons) {
				columns.push({
					title: localize("_Reason"),
					trackingId: "reason",
					width: "fill"
				});
			}

			return columns;
		},
		[showReasons]
	);

	const items = useMemo(
		(): readonly FailedItemWithId[] => failedItems.map(
			x => ({
				...x,
				id: newGuid()
			})
		),
		[failedItems]
	);

	return (
		<Dialog trackingId="not-all-items-were-deleted-dialog">
			<Dialog.Header
				title={localize("_NotAllItemsWereDeleted")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p>{localize("_TheFollowingItemsCouldNotBeDeleted")}</p>
				<TableView
					columns={columnDefinitions}
					items={items}
					itemKey="id"
					trackingId="not-deleted-items"
				>
					{(x): ReactElement => (
						<TableView.Row
							trackingId={x.id}
						>
							<TableView.Cell>
								{x.name}
							</TableView.Cell>
							<TableView.Cell>
								{x.reason}
							</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={onClose}
						text={localize("_Close")}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

NotAllItemsWereDeletedDialog.displayName = "NotAllItemsWereDeletedDialog";

export type FailedItem = {
	name: string;
	reason?: string;
};

type FailedItemWithId = FailedItem & {
	id: Guid;
};

type NotAllItemsWereDeletedDialogInitialData = Omit<NotAllItemsWereDeletedDialogProps, "onClose">;

export function openNotAllItemsWereDeletedDialog(
	props: NotAllItemsWereDeletedDialogInitialData
): void {
	renderReactDialog((onClose) => (
		<NotAllItemsWereDeletedDialog {...props} onClose={onClose} />
	));
}